import React, { useState, useEffect, useCallback } from 'react';
import { usePinToBoard } from '../contexts/PinToBoardContext';

// Get API base URL from environment variable
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const PdfTable = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'created_date', direction: 'desc' });
  const [filters, setFilters] = useState({ provider: '', file_name: '' });
  const [pagination, setPagination] = useState({ total: 0, limit: 50, offset: 0, pages: 0 });
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [deleteSuccess, setDeleteSuccess] = useState(null);
  const { pinItem } = usePinToBoard();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        sort: sortConfig.key,
        order: sortConfig.direction,
        limit: pagination.limit,
        offset: pagination.offset
      });

      if (filters.provider) params.append('provider', filters.provider);
      if (filters.file_name) params.append('file_name', filters.file_name);

      const response = await fetch(`${API_BASE_URL}/api/data/pdf?${params}`, {
        credentials: 'include'
      });

      const result = await response.json();

      if (result.success) {
        setData(result.data);
        setPagination(result.pagination);
        setError(null);
      } else {
        setError(result.message || 'Failed to fetch data');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Error fetching PDF data:', err);
    } finally {
      setLoading(false);
    }
  }, [sortConfig, filters, pagination.offset, pagination.limit]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
    setPagination(prev => ({ ...prev, offset: 0 })); // Reset to first page
  };

  const handleFilterChange = (filterKey, value) => {
    setFilters(prev => ({ ...prev, [filterKey]: value }));
    setPagination(prev => ({ ...prev, offset: 0 })); // Reset to first page
  };

  const handlePageChange = (newOffset) => {
    setPagination(prev => ({ ...prev, offset: newOffset }));
  };

  const handleItemsPerPageChange = (newLimit) => {
    setItemsPerPage(newLimit);
    setPagination(prev => ({ ...prev, limit: newLimit, offset: 0 })); // Reset to first page
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) return '↕️';
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const getFileExtension = (filename) => {
    if (!filename) return '';
    const ext = filename.split('.').pop().toLowerCase();
    return ext;
  };

  const isLocalFile = (fileName) => {
    if (!fileName) return false;
    // If it's a URL (starts with http/https), it's NOT a local file
    const isUrl = fileName.startsWith('http://') || fileName.startsWith('https://');
    return !isUrl;
  };

  const getCleanFileName = (fileName) => {
    if (!fileName) return '';
    if (!isLocalFile(fileName)) return fileName;
    // Split by both / and \ for cross-platform compatibility
    const parts = fileName.split(/[\\/]/);
    return parts[parts.length - 1];
  };

  const getFileIcon = (filename) => {
    const ext = getFileExtension(filename);
    switch (ext) {
      case 'pdf':
        return '📄';
      case 'doc':
      case 'docx':
        return '📝';
      case 'xls':
      case 'xlsx':
        return '📊';
      case 'ppt':
      case 'pptx':
        return '📋';
      default:
        return '📄';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      // Check if date is valid
      if (isNaN(date.getTime())) return 'N/A';
      // Format as dd/mm/YYYY
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'N/A';
    }
  };

  const handleDownload = async (e, id, fileName) => {
    // If it's an external link, let the default <a> tag behavior handle it
    if (!isLocalFile(fileName)) return;
    
    e.preventDefault();
    try {
      // Request a secure download token from the backend
      const response = await fetch(`${API_BASE_URL}/api/files/${id}/download`, {
        credentials: 'include'
      });
      
      const result = await response.json();
      
      if (result.success && result.url) {
        // The backend returns a URL pointing to the file server with a one-time token
        // e.g., http://localhost:8080/download/123?token=xyz
        
        // Trigger the download by creating a temporary link
        const link = document.createElement('a');
        link.href = result.url;
        // Setting download attribute suggests to the browser that it should download rather than navigate
        link.setAttribute('download', result.filename || '');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        console.error('Download error:', result.error);
        alert(result.error || 'Failed to generate download link');
      }
    } catch (err) {
      console.error('Error downloading file:', err);
      alert('Network error. Please try again.');
    }
  };

  const handleDelete = async (id, fileName) => {
    if (!window.confirm(`Are you sure you want to delete "${fileName}"?`)) {
      return;
    }

    try {
      setDeleteLoading(id);
      setDeleteError(null);
      setDeleteSuccess(null);

      const response = await fetch(`${API_BASE_URL}/api/files/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      const result = await response.json();

      if (result.success) {
        setDeleteSuccess(`"${result.data.file_name}" deleted successfully`);
        // Refresh data
        fetchData();
      } else {
        setDeleteError(result.message || 'Failed to delete record');
      }
    } catch (err) {
      setDeleteError('Network error. Please try again.');
      console.error('Error deleting PDF record:', err);
    } finally {
      setDeleteLoading(null);
    }
  };

  if (loading && data.length === 0) {
    return (
      <div className="table-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading PDF data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="table-container">
      <div className="table-header">
        <h2>PDF Documents</h2>
        <p className="table-subtitle">
          PDF documents and files collected from various real estate sources
        </p>
      </div>

      {/* Filters */}
      <div className="table-filters">
        <div className="filter-group">
          <label>Provider:</label>
          <input
            type="text"
            placeholder="Filter by provider..."
            value={filters.provider}
            onChange={(e) => handleFilterChange('provider', e.target.value)}
          />
        </div>
        <div className="filter-group">
          <label>File Name:</label>
          <input
            type="text"
            placeholder="Filter by file name..."
            value={filters.file_name}
            onChange={(e) => handleFilterChange('file_name', e.target.value)}
          />
        </div>
        <div className="filter-group">
          <label htmlFor="pdf-items-per-page">Items per page:</label>
          <select
            id="pdf-items-per-page"
            value={itemsPerPage}
            onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
            className="items-per-page-select"
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>
        </div>
        <button 
          className="filter-clear"
          onClick={() => {
            setFilters({ provider: '', file_name: '' });
          }}
        >
          Clear Filters
        </button>
      </div>

      {error && (
        <div className="error-message">
          <p>⚠️ {error}</p>
          <button onClick={fetchData}>Retry</button>
        </div>
      )}
      
      {deleteError && (
        <div className="error-message">
          <p>⚠️ {deleteError}</p>
          <button onClick={() => setDeleteError(null)}>Dismiss</button>
        </div>
      )}
      
      {deleteSuccess && (
        <div className="success-message">
          <p>✅ {deleteSuccess}</p>
          <button onClick={() => setDeleteSuccess(null)}>Dismiss</button>
        </div>
      )}

      {data.length === 0 && !loading ? (
        <div className="no-data">
          <p>📄 No PDF documents found</p>
          <p>Documents will appear here once the bot collects PDF files based on your requests.</p>
        </div>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('created_date')} className="sortable">
                    Date {getSortIcon('created_date')}
                  </th>
                  <th>
                    Query
                  </th>
                  <th onClick={() => handleSort('file_name')} className="sortable">
                    File Name {getSortIcon('file_name')}
                  </th>
                  <th onClick={() => handleSort('provider')} className="sortable">
                    Provider {getSortIcon('provider')}
                  </th>
                  <th onClick={() => handleSort('page_number')} className="sortable">
                    Pages {getSortIcon('page_number')}
                  </th>
                  <th>Information</th>
                  <th>Download</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <div className="file-name-cell">
                        <span className="file-info">📅</span>
                        <span>{formatDate(item.created_date)}</span>
                      </div>
                    </td>
                    <td>
                        <span className="file-info">🔍</span>
                        <span>{item.query}</span>
                    </td>
                    <td className="file-name-cell">
                      <div className="file-info">
                        <span className="file-icon">{getFileIcon(item.file_name)}</span>
                        <div className="file-details">
                          {!isLocalFile(item.file_name) ? (
                            <a 
                              href={item.file_name} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="file-name-link"
                            >
                              {truncateText(item.file_name || item.url, 40)}
                            </a>
                          ) : (
                            <strong>{truncateText(getCleanFileName(item.file_name), 40)}</strong>
                          )}
                          <small className="file-ext">{getFileExtension(item.file_name || item.url).toUpperCase()}</small>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="provider-tag">{item.provider || 'N/A'}</span>
                    </td>
                    <td className="pages-cell">
                      {item.page_number ? (
                        <span className="page-count">
                          📖 {item.page_number} {item.page_number === 1 ? 'page' : 'pages'}
                        </span>
                      ) : (
                        'N/A'
                      )}
                    </td>
                    <td className="info-cell">
                      {item.info ? (
                        <div className="info-content">
                          {truncateText(item.info, 120)}
                        </div>
                      ) : (
                        <span className="no-info">No additional information</span>
                      )}
                    </td>
                    <td className="download-cell">
                      {item.file_name ? (
                        isLocalFile(item.file_name) ? (
                          <button 
                            className="download-link"
                            onClick={(e) => handleDownload(e, item.id, item.file_name)}
                          >
                            <span className="download-icon">⬇️</span>
                            Download
                          </button>
                        ) : (
                          <a 
                            href={item.file_name} 
                            target="_blank"
                            rel="noopener noreferrer"
                            className="download-link"
                          >
                            <span className="download-icon">⬇️</span>
                            Download
                          </a>
                        )
                      ) : (
                        <span className="no-download">N/A</span>
                      )}
                    </td>
                    <td className="actions-cell">
                      <button
                        className="pin-btn"
                        title="Pin to Board"
                        onClick={(e) =>
                          pinItem(
                            {
                              item_type: 'pdf',
                              ref_id: String(item.id),
                              title: getCleanFileName(item.file_name) || item.file_name || 'Document',
                              metadata: {
                                provider: item.provider,
                                file_name: item.file_name,
                                page_number: item.page_number,
                                query: item.query,
                              },
                            },
                            e.currentTarget,
                          )
                        }
                      >
                        <i className="fas fa-thumbtack" />
                      </button>
                      <button
                        onClick={() => handleDelete(item.id, item.file_name || item.url)}
                        disabled={deleteLoading === item.id}
                        className="delete-btn"
                        title="Delete record"
                      >
                        {deleteLoading === item.id ? '⏳' : '🗑️'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="mobile-cards">
            {data.map((item) => (
              <div key={item.id} className="mobile-card">
                <div className="mobile-card-header">
                  <div className="file-info">
                    <span className="file-icon">{getFileIcon(item.file_name)}</span>
                    <div className="file-details">
                      <h3 className="mobile-card-title">
                        {!isLocalFile(item.file_name) ? (
                          <a 
                            href={item.file_name || item.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="file-name-link"
                          >
                            {truncateText(item.file_name || item.url, 50)}
                          </a>
                        ) : (
                          truncateText(getCleanFileName(item.file_name), 50)
                        )}
                      </h3>
                      <span className="file-ext">{getFileExtension(item.file_name || item.url).toUpperCase()}</span>
                    </div>
                  </div>
                </div>
                
                <div className="mobile-card-content">
                  <div className="mobile-card-field">
                    <span className="mobile-card-label">Provider</span>
                    <span className="mobile-card-value">
                      <span className="provider-tag">{item.provider || 'N/A'}</span>
                    </span>
                  </div>
                  
                  <div className="mobile-card-field">
                    <span className="mobile-card-label">Pages</span>
                    <span className="mobile-card-value">
                      {item.page_number ? (
                        <span className="page-count">
                          📖 {item.page_number} {item.page_number === 1 ? 'page' : 'pages'}
                        </span>
                      ) : (
                        'N/A'
                      )}
                    </span>
                  </div>
                  
                  {item.info && (
                    <div className="mobile-card-field full-width">
                      <span className="mobile-card-label">Information</span>
                      <span className="mobile-card-value">
                        {truncateText(item.info, 120)}
                      </span>
                    </div>
                  )}
                </div>
                
                {(item.file_name || item.url) && (
                  <div className="mobile-card-actions">
                    {isLocalFile(item.file_name || item.url) ? (
                      <button 
                        className="download-link"
                        onClick={(e) => handleDownload(e, item.id, item.file_name || item.url)}
                      >
                        <span className="download-icon">⬇️</span>
                        Download PDF
                      </button>
                    ) : (
                      <a 
                        href={item.file_name || item.url} 
                        target="_blank"
                        rel="noopener noreferrer"
                        className="download-link"
                      >
                        <span className="download-icon">⬇️</span>
                        Download PDF
                      </a>
                    )}
                    <button
                      onClick={() => handleDelete(item.id, item.file_name || item.url)}
                      disabled={deleteLoading === item.id}
                      className="delete-btn mobile-delete-btn"
                      title="Delete record"
                    >
                      {deleteLoading === item.id ? '⏳' : '🗑️'}
                    </button>
                  </div>
                )}
                {!item.url && (
                  <div className="mobile-card-actions">
                    <button
                      onClick={() => handleDelete(item.id, item.file_name || item.url)}
                      disabled={deleteLoading === item.id}
                      className="delete-btn mobile-delete-btn"
                      title="Delete record"
                    >
                      {deleteLoading === item.id ? '⏳' : '🗑️'}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="pagination">
              <div className="pagination-info">
                Showing {pagination.offset + 1}-{Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total} files
              </div>
              <div className="pagination-controls">
                <button
                  onClick={() => handlePageChange(0)}
                  disabled={pagination.offset === 0}
                >
                  First
                </button>
                <button
                  onClick={() => handlePageChange(pagination.offset - pagination.limit)}
                  disabled={pagination.offset === 0}
                >
                  Previous
                </button>
                <span className="page-info">
                  Page {Math.floor(pagination.offset / pagination.limit) + 1} of {pagination.pages}
                </span>
                <button
                  onClick={() => handlePageChange(pagination.offset + pagination.limit)}
                  disabled={pagination.offset + pagination.limit >= pagination.total}
                >
                  Next
                </button>
                <button
                  onClick={() => handlePageChange((pagination.pages - 1) * pagination.limit)}
                  disabled={pagination.offset + pagination.limit >= pagination.total}
                >
                  Last
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {loading && data.length > 0 && (
        <div className="loading-overlay">
          <div className="spinner"></div>
        </div>
      )}
    </div>
  );
};

export default PdfTable;
